/**
 * The side menu's and the user menu's pure pieces (CU-869fmherf). Pure and alias-free, so `node --test`
 * runs them; the paths come from `USER_ROUTES` at the call site.
 */

// What the three nav views paint as a separator (`Nav/NavSection/Vertical`, `Mini`, `PopoverNav`)
export const MENU_DIVIDER = Object.freeze({ path: 'divider' })

/**
 * The user menu's links between Home and Logout (D2): Settings for everyone, Iris settings for Iris's
 * users. `iris: true` asks the caller for Iris's mark as the icon.
 */
export function userMenuLinks({ isIris, settingsPath, irisPath }) {
	return [
		{ url: settingsPath, icon: 'gear', text: 'Settings' },
		...(isIris ? [{ url: irisPath, iris: true, text: 'Iris settings' }] : []),
	]
}

/**
 * The menu with a separator right above User Settings, so the settings (User Settings, Admin) sit apart
 * at the end (D5). Applied to the menu only, after the feature filter: it never reaches `useRoutes` and
 * needs no `id`. Nothing above User Settings, no User Settings or a divider already there: unchanged.
 */
export function withSettingsDivider(routes, beforePath) {
	const at = routes.findIndex(r => r.path === beforePath)
	if (at <= 0 || routes[at - 1]?.path === MENU_DIVIDER.path) return routes
	return [...routes.slice(0, at), MENU_DIVIDER, ...routes.slice(at)]
}

/**
 * The entries that wear "NEW" in the side menu, and until when — one list for every section (BI, Analyzers, User
 * Settings), read by `newBadge` (CU-869fmjzrg decision 13, TL N2; shared since CU-869fmherf batch 5). `null` = not dated
 * yet: it shows. Leave it `null` on `testing`; set the date (about a month after the release) when the entry is
 * cherry-picked to `master`, so it never expires before PROD sees it.
 */
export const NEW_UNTIL = Object.freeze({
	seasonVsLastYear: null,
	priceCurve: null,
	officeNetwork: null,
	rankAnalyzer: null,
	iris: null,
	lastChanges: null,
})

/**
 * The "NEW" mark of a menu entry. `newUntil` = `{ key: 'YYYY-MM-DD' | null }`: an entry listed there wears it until
 * that day included, and then it goes on its own; `null` shows. Pure: `today` comes from the caller (no date, no mark).
 */
export function menuBadge(key, today, newUntil = {}) {
	if (!Object.prototype.hasOwnProperty.call(newUntil || {}, key) || !(today instanceof Date)) return null
	const until = newUntil[key]
	if (until === null) return 'NEW'
	const pad = n => String(n).padStart(2, '0')
	const iso = `${today.getFullYear()}-${pad(today.getMonth() + 1)}-${pad(today.getDate())}`
	return iso <= until ? 'NEW' : null
}

/** `{ badge: 'NEW' }` to spread into a route while it wears the mark, `{}` otherwise (the nav views read `badge`). */
export function newBadge(key, today, newUntil = NEW_UNTIL) {
	const badge = menuBadge(key, today, newUntil)
	return badge ? { badge } : {}
}

/**
 * The menu with `entry` listed inside the section `sectionPath`, right after its child `afterPath` (at the end when
 * that child is not there). Menu only, like the divider: the entry's route lives elsewhere (CU-869fmherf batch 5:
 * Europcar Custom Search Analytics is listed under Main Page, its URL stays `/business-logistics/europcar`). No
 * section, or the entry already there: unchanged. Never mutates its input.
 */
export function withMenuEntry(routes, sectionPath, afterPath, entry) {
	const at = routes.findIndex(r => r.path === sectionPath)
	if (at < 0) return routes
	const children = routes[at].children || []
	if (children.some(c => c.path === entry.path && !c.noMenu)) return routes
	const after = children.findIndex(c => c.path === afterPath)
	const pos = after < 0 ? children.length : after + 1
	const section = { ...routes[at], children: [...children.slice(0, pos), entry, ...children.slice(pos)] }
	return [...routes.slice(0, at), section, ...routes.slice(at + 1)]
}
